import { authActions, boAuthActions } from '@mkp/auth/actions';
import { CompanyProfile } from '@mkp/company/models';
import * as companyActions from '@mkp/company/state/actions';
import {
  CompanyApiActions,
  companyApiActions,
  companyExistsGuardActions,
  companyFormPageActions,
} from '@mkp/company/state/actions';
import { RoutingFactoryActions } from '@mkp/layout/shell-user-layout/actions';
import { legalEntityDetailsActions } from '@mkp/legal-entity/actions';
import { ApiLink, UUID } from '@mkp/shared/models';
import { FetchState, LoadingState } from '@mkp/shared/util-state';
import { accountAggregateApiActions } from '@mkp/user-account-membership/actions';
import { FeatureDashboardActions } from '@mkp/user/feature-dashboard/actions';
import { EntityState, createEntityAdapter } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

const featureKey = 'companyState';

export interface CompanyState extends EntityState<CompanyProfile> {
  selectedId: string | undefined;
  fetchState: FetchState<{ message: string }>;
  totalCount?: number;
  _links?: { [key: string]: ApiLink };
  filter?: string;
}

const adapter = createEntityAdapter<CompanyProfile>();

export const initialState: CompanyState = adapter.getInitialState({
  selectedId: undefined,
  fetchState: LoadingState.INIT,
  totalCount: undefined,
  _links: undefined,
  filter: undefined,
});

const ResetCompanyProfile = (state: CompanyState) => {
  return adapter.removeAll({ ...state, ...initialState });
};

const CompanyUpdateProfileSuccess = (
  state: CompanyState,
  profile: Partial<CompanyProfile>
): CompanyState => {
  if (profile?.id) {
    return adapter.upsertOne(profile as CompanyProfile, {
      ...state,
      fetchState: LoadingState.LOADED,
    });
  }

  return {
    ...state,
    fetchState: LoadingState.LOADED,
  };
};

const CompanyGetProfileListSuccess = (
  state: CompanyState,
  results: CompanyProfile[],
  _links: { [key: string]: ApiLink },
  filter: string,
  totalCount: number
) => {
  return adapter.upsertMany(results, {
    ...state,
    fetchState: LoadingState.LOADED,
    _links,
    filter,
    totalCount,
  });
};

const CompanyLocationDeleteSuccess = (
  state: CompanyState,
  { locationId, companyProfileId }: { locationId: UUID; companyProfileId: string }
) => {
  const existingCompany = state.entities[companyProfileId];
  if (!existingCompany) return state;

  const locations =
    existingCompany.locations?.filter((location) => location.id !== locationId) ?? [];

  return adapter.updateOne(
    {
      id: companyProfileId,
      changes: { locations },
    },
    {
      ...state,
      fetchState: LoadingState.LOADED,
    }
  );
};

const _companyReducer = createReducer(
  initialState,
  on(companyActions.ResetCompanyProfile, (state) => ResetCompanyProfile(state)),
  on(
    RoutingFactoryActions.enter,
    FeatureDashboardActions.enter,
    legalEntityDetailsActions.pageEntered,
    (state) => ({
      ...state,
      fetchState: LoadingState.LOADING,
    })
  ),
  on(
    companyActions.CompanyGetProfileListSuccess,
    (state, { companyProfiles, _links, filter, totalCount }) =>
      CompanyGetProfileListSuccess(state, companyProfiles, _links, filter, totalCount)
  ),
  on(
    companyApiActions.loadCompanySuccess,
    companyApiActions.loadCompanyIfNotExistSuccess,
    companyApiActions.updateCompanySuccess,
    (state, { company }) => CompanyUpdateProfileSuccess(state, company)
  ),
  on(companyApiActions.updateCompanyNoChanges, (state) => ({
    ...state,
    fetchState: LoadingState.LOADED,
  })),
  on(
    authActions.loadCompaniesAfterLoginSuccess,
    authActions.loadWorkspaceCompanySuccess,
    (state, { companyProfiles }) =>
      adapter.upsertMany(companyProfiles, {
        ...state,
        selectedId: companyProfiles.length === 1 ? companyProfiles[0].id : undefined,
        fetchState: LoadingState.LOADED,
      })
  ),
  // happens when a user deletes their own roles: we do a setAll on companies to remove the deleted ones
  on(
    CompanyApiActions.refreshAllCompaniesSuccess,
    accountAggregateApiActions.refreshOnPermissionCheckSuccess,
    accountAggregateApiActions.refreshOnRedeemInviteSuccess,
    (state, { companyProfiles }) =>
      adapter.setAll(companyProfiles, {
        ...state,
        selectedId: companyProfiles.length === 1 ? companyProfiles[0].id : undefined,
        fetchState: LoadingState.LOADED,
      })
  ),
  on(
    accountAggregateApiActions.refreshOnPermissionCheckFailure,
    accountAggregateApiActions.refreshOnRedeemInviteFailure,
    (state) =>
      adapter.setAll([], { ...state, selectedId: undefined, fetchState: LoadingState.LOADED })
  ),
  on(CompanyApiActions.loadCompaniesSuccess, (state, { companies, selectedId }) =>
    adapter.setAll(companies, {
      ...state,
      selectedId: selectedId || undefined,
      fetchState: LoadingState.LOADED,
    })
  ),
  on(legalEntityDetailsActions.companiesLoadSuccess, (state, { companies }) =>
    adapter.setAll(companies, {
      ...state,
      selectedId: companies.length === 1 ? companies[0].id : undefined,
      fetchState: LoadingState.LOADED,
    })
  ),
  on(companyExistsGuardActions.canActivate, (state, { companyId }) =>
    state.entities[companyId] ? state : { ...state, fetchState: LoadingState.LOADING }
  ),
  on(
    authActions.loadActiveAccountMembershipsSuccess,
    authActions.workspaceFoundInStore,
    authActions.loadWorkspaceAccountSuccess,
    companyActions.CompanyGetProfileList,
    companyFormPageActions.updateCompany,
    (state) => ({ ...state, fetchState: LoadingState.LOADING })
  ),
  on(companyApiActions.deleteLocationSuccess, (state, { id, companyProfileId }) =>
    CompanyLocationDeleteSuccess(state, { locationId: id, companyProfileId })
  ),
  on(
    authActions.loadAccountsAfterLoginFailed,
    authActions.loadWorkspaceCompanyError,
    companyActions.CompanyGetProfileListFailure,
    companyApiActions.loadCompanyFailure,
    companyApiActions.loadCompanyIfNotExistFailure,
    companyApiActions.updateCompanyFailure,
    CompanyApiActions.loadCompaniesFailure,
    legalEntityDetailsActions.companiesLoadFailure,
    (state, { error }) => ({ ...state, fetchState: { error } as FetchState<{ message: string }> })
  ),
  on(authActions.workspaceSelected, (state, { companyProfileId }) => ({
    ...state,
    selectedId: companyProfileId,
  })),
  on(boAuthActions.workspaceUnselected, (state) => ({
    ...state,
    selectedId: undefined,
  }))
);

function reducer(state: CompanyState, action: Action): CompanyState {
  return _companyReducer(state, action);
}

export { adapter, featureKey, reducer };
